import type { ContactFilter } from "@/lib/types";
import { brandById } from "@/lib/brands";
import { formatCents, formatDate } from "@/lib/format";

/** The contacts list keeps its filters in the URL, so a filtered list can be bookmarked or shared. */
export type ContactsSearch = ContactFilter & { saved?: number; sort?: string };

export const SORTS: { value: string; label: string }[] = [
  { value: "newest", label: "Newest first" },
  { value: "name", label: "Name (A to Z)" },
  { value: "ltv", label: "Lifetime value (highest first)" },
  { value: "last_order", label: "Last order (latest first)" },
  { value: "added", label: "Date added (latest first)" },
  { value: "oldest", label: "Oldest first" },
];

export const EMAIL_STATUSES: { value: string; label: string }[] = [
  { value: "mailable", label: "Has a mailable address" },
  { value: "no_email", label: "No email address" },
  { value: "opted_out", label: "Opted out" },
  { value: "bounced", label: "Bounced" },
  { value: "complained", label: "Complained" },
  { value: "unconfirmed", label: "Unconfirmed" },
  { value: "invalid", label: "Invalid" },
  { value: "suppressed", label: "Suppressed" },
];

const strKeys = ["q", "email_status", "access", "created_since", "created_until", "last_order_since", "last_order_until", "lead_source", "utm_source", "utm_campaign", "sort"] as const;
const numKeys = ["tag_id", "brand_id", "ltv_min_cents", "ltv_max_cents", "affiliate_id", "saved"] as const;
const listKeys = ["tags_any", "tags_all", "tags_none"] as const;

function toNum(v: unknown): number | undefined {
  if (typeof v === "number" && Number.isFinite(v)) return v;
  if (typeof v === "string" && /^-?\d+$/.test(v)) return Number(v);
  return undefined;
}

function toList(v: unknown): number[] | undefined {
  const arr = Array.isArray(v) ? v : typeof v === "string" ? v.split(",") : typeof v === "number" ? [v] : [];
  const out = arr.map(toNum).filter((n): n is number => n !== undefined && n > 0);
  return out.length ? out : undefined;
}

export function parseContactsSearch(s: Record<string, unknown>): ContactsSearch {
  const out: ContactsSearch = {};
  for (const k of strKeys) if (typeof s[k] === "string" && s[k]) (out as Record<string, unknown>)[k] = s[k];
  for (const k of numKeys) {
    const n = toNum(s[k]);
    if (n !== undefined) (out as Record<string, unknown>)[k] = n;
  }
  for (const k of listKeys) {
    const l = toList(s[k]);
    if (l) out[k] = l;
  }
  if (s.archived === true || s.archived === "true") out.archived = true;
  if (s.subscribed === true || s.subscribed === "true") out.subscribed = true;
  if (s.customer === "yes" || s.customer === "no") out.customer = s.customer;
  return out;
}

/** The filter part of the search (no sort / saved search id). */
export function filterOf(s: ContactsSearch): ContactFilter {
  const { saved: _saved, sort: _sort, ...f } = s;
  void _saved;
  void _sort;
  return f;
}

export function isEmptyFilter(f: ContactFilter): boolean {
  return Object.values(f).every((v) => v === undefined || v === "" || v === false || (Array.isArray(v) && v.length === 0));
}

/** Query-string values for contacts_list (arrays as comma lists). */
export function filterParams(f: ContactFilter): Record<string, string | number | boolean | undefined> {
  const p: Record<string, string | number | boolean | undefined> = {};
  for (const [k, v] of Object.entries(f)) {
    if (v === undefined || v === "" || v === false) continue;
    p[k] = Array.isArray(v) ? v.join(",") : (v as string | number | boolean);
  }
  return p;
}

/** Plain-language pieces of a filter, for chips and saved-search descriptions. */
export function describeFilter(f: ContactFilter, tagName: (id: number) => string): { key: keyof ContactFilter; text: string }[] {
  const out: { key: keyof ContactFilter; text: string }[] = [];
  const tags = (ids: number[] | undefined) => (ids ?? []).map(tagName).join(", ");
  if (f.archived) out.push({ key: "archived", text: "Archive only" });
  if (f.q) out.push({ key: "q", text: `Text: "${f.q}"` });
  if (f.tag_id) out.push({ key: "tag_id", text: `Tag: ${tagName(f.tag_id)}` });
  if (f.tags_any?.length) out.push({ key: "tags_any", text: `Any of: ${tags(f.tags_any)}` });
  if (f.tags_all?.length) out.push({ key: "tags_all", text: `All of: ${tags(f.tags_all)}` });
  if (f.tags_none?.length) out.push({ key: "tags_none", text: `None of: ${tags(f.tags_none)}` });
  if (f.brand_id) out.push({ key: "brand_id", text: `Brand: ${brandById(f.brand_id)?.short ?? f.brand_id}` });
  if (f.subscribed) out.push({ key: "subscribed", text: "Subscribed to marketing" });
  if (f.email_status) out.push({ key: "email_status", text: EMAIL_STATUSES.find((e) => e.value === f.email_status)?.label ?? f.email_status });
  if (f.customer) out.push({ key: "customer", text: f.customer === "yes" ? "Customers" : "Never bought" });
  if (f.access) out.push({ key: "access", text: `Access: ${f.access.replace(/_/g, " ")}` });
  if (f.ltv_min_cents) out.push({ key: "ltv_min_cents", text: `Lifetime value ≥ ${formatCents(f.ltv_min_cents)}` });
  if (f.ltv_max_cents) out.push({ key: "ltv_max_cents", text: `Lifetime value ≤ ${formatCents(f.ltv_max_cents)}` });
  if (f.created_since) out.push({ key: "created_since", text: `Added since ${formatDate(f.created_since)}` });
  if (f.created_until) out.push({ key: "created_until", text: `Added by ${formatDate(f.created_until)}` });
  if (f.last_order_since) out.push({ key: "last_order_since", text: `Ordered since ${formatDate(f.last_order_since)}` });
  if (f.last_order_until) out.push({ key: "last_order_until", text: `Last order by ${formatDate(f.last_order_until)}` });
  if (f.lead_source) out.push({ key: "lead_source", text: `Lead source: ${f.lead_source}` });
  if (f.utm_source) out.push({ key: "utm_source", text: `UTM source: ${f.utm_source}` });
  if (f.utm_campaign) out.push({ key: "utm_campaign", text: `UTM campaign: ${f.utm_campaign}` });
  if (f.affiliate_id) out.push({ key: "affiliate_id", text: `Affiliate #${f.affiliate_id}` });
  return out;
}
